import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// RentOcart Logo URL (dark version)
const LOGO_URL = "https://customer-assets.emergentagent.com/job_86ba7c87-2c5f-4809-b4ca-291f6e117aa2/artifacts/wjmihcrt_black%20rento%20logo.png";

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password);
    
    if (result.success) {
      toast.success('Login successful!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[#2a2a2a]">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1a1a1a] flex-col items-center justify-center p-12 relative overflow-hidden">
        {/* Decorative gradient circles */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-64 h-64 bg-orange-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-green-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 text-center">
          <img 
            src={LOGO_URL} 
            alt="rentOcart.com" 
            className="h-32 md:h-40 object-contain mx-auto mb-8"
          />
          <p className="text-xl text-gray-300 max-w-md leading-relaxed">
            Quality Control System for Electronics Rental
          </p>
          <p className="mt-4 text-gray-500 text-sm">
            Rent · Relax · Return
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#2a2a2a]">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center lg:hidden">
            <img 
              src={LOGO_URL} 
              alt="rentOcart.com" 
              className="h-20 object-contain mx-auto mb-6"
            />
          </div>
          
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-2 text-white">Welcome Back</h2>
            <p className="text-gray-400">Sign in to continue to QC System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-base font-medium text-gray-300">
                Username
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="h-14 text-lg bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-base font-medium text-gray-300">
                  Password
                </Label>
                <Link to="/forgot-password" className="text-sm text-cyan-400 hover:text-cyan-300 hover:underline">
                  Forgot Password?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-14 text-lg bg-[#1a1a1a] border-gray-700 text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-cyan-500"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full h-14 text-lg font-bold uppercase tracking-wide bg-gradient-to-r from-cyan-500 via-green-500 to-orange-500 hover:from-cyan-600 hover:via-green-600 hover:to-orange-600 text-white shadow-lg"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-8">
            rentOcart.com · Rent-Relax-Return
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;